/* ag-dialog (a11y batch 1b, 2026-10-20; design brief 11.13 "Dialogs and drawers"): keyboard focus for the theme's
   slide-in dialogs, the cart drawer first (1c adds the other modals with agDialog.watch).
   - Shut: the dialog is inert, so its links and buttons leave the Tab order and the accessibility tree (the closed
     cart drawer sat offscreen in the Tab order on every page).
   - Opening: focus moves to the dialog itself (tabindex=-1 + aria-label, so a screen reader names it) unless the opener
     already put focus inside; Tab and Shift+Tab stay inside while it is open.
   - Shutting: focus goes back to whatever had it before (the cart button, an add-to-cart button), if focus was in the
     dialog or nowhere.
   It watches the open class instead of wrapping open()/close(), because several scripts open the cart drawer
   (cart-drawer.js open(), agcf.js, agbis.js, agwc-theme.js). Escape is already handled by cart-drawer.js. */
(function () {
  'use strict';
  if (window.agDialog) return;
  var FOCUSABLE = 'a[href],area[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),' +
    'textarea:not([disabled]),iframe,summary,[contenteditable="true"],[tabindex]:not([tabindex="-1"])';

  function tabbable(d) {
    var radios = {};
    return Array.prototype.filter.call(d.querySelectorAll(FOCUSABLE), function (el) {
      if (!el.getClientRects().length || el.closest('[inert]') !== d.closest('[inert]')) return false;
      var cs = window.getComputedStyle(el);
      if (cs.visibility === 'hidden' || cs.display === 'none') return false;
      if (el.type === 'radio' && el.name) {           // one stop per radio group: the checked one, else the first
        var g = d.querySelectorAll('input[type="radio"][name="' + el.name.replace(/"/g, '\\"') + '"]');
        var pick = Array.prototype.filter.call(g, function (r) { return r.checked; })[0] || g[0];
        if (radios[el.name] || el !== pick) return false;
        radios[el.name] = true;
      }
      return true;
    });
  }

  // opts.when(d): behave as a dialog only while it returns true (1c: the filter column is a drawer on phones only, a plain
  // sidebar on desktop, which must never go inert). opts.close(d): what Escape does while open, for a dialog whose own
  // script has no Escape. opts.opener(d): where focus goes back when the opener is unknown. A dialog re-rendered by its page (the filter refresh replaces it) is simply watched again; the
  // old element's listeners drop themselves once it leaves the document.
  function watch(d, isOpen, opts) {
    if (!d || d._agDialog) return;
    opts = opts || {};
    var applies = function () { return !opts.when || opts.when(d); };
    var state = function () { return applies() && isOpen(d); };
    var open = state(), opener = null;
    d._agDialog = true;
    d.inert = applies() && !open;

    // Tab is moved by hand, never left to the browser: Safari's default Tab skips links, so its "next" stop was outside
    // the drawer and focus fell to the page (WebKit probe 2026-10-20).
    function onKey(e) {
      if (!d.isConnected) { document.removeEventListener('keydown', onKey, true); return; }
      if (e.key === 'Escape' && opts.close) { opts.close(d); return; }
      if (e.key !== 'Tab' || e.ctrlKey || e.metaKey) return;   // Option+Tab (Safari's link Tab) is held too
      var f = tabbable(d);
      e.preventDefault();
      if (!f.length) { d.focus(); return; }
      var i = f.indexOf(document.activeElement);
      var n = e.shiftKey ? (i <= 0 ? f.length - 1 : i - 1) : (i < 0 || i === f.length - 1 ? 0 : i + 1);
      f[n].focus();
    }
    if (open) document.addEventListener('keydown', onKey, true);   // re-rendered while open (its page put focus back inside)

    function change() {
      if (!d.isConnected) { window.removeEventListener('resize', change); return; }
      var now = state();
      if (now === open) { d.inert = applies() && !open; return; }   // e.g. crossing the breakpoint while shut
      open = now;
      if (now) {
        var a = document.activeElement;
        opener = a && a !== document.body && !d.contains(a) ? a : null;
        d.inert = false;
        document.addEventListener('keydown', onKey, true);
        // the dialog itself when it can take focus (it has a name), else its first control. A dialog still turning
        // visible takes no focus, so the move is tried again every 50ms for up to 2s, stopping once focus is in
        // (WebKit, 2026-10-21: the shipping drawer was still visibility:hidden at 60ms, at times past 600ms on a reopen,
        // and focus stayed on the link behind it).
        var tries = 0;
        setTimeout(function moveIn() {
          if (!open || d.contains(document.activeElement)) return;
          (d.hasAttribute('tabindex') ? d : (tabbable(d)[0] || d)).focus({ preventScroll: true });
          if (!d.contains(document.activeElement) && ++tries < 40) setTimeout(moveIn, 50);
        }, 60);
      } else {
        document.removeEventListener('keydown', onKey, true);
        var inside = d.contains(document.activeElement) || document.activeElement === document.body;
        d.inert = applies();
        var back = opener && (document.contains(opener) ? opener : (opener.id && document.getElementById(opener.id)));
        if (!back && opts.opener) back = opts.opener(d);   // e.g. re-rendered while open: it never saw who opened it
        if (inside && back) back.focus({ preventScroll: true });
        opener = null;
      }
    }
    new MutationObserver(change).observe(d, { attributes: true, attributeFilter: opts.attrs || ['class'] });   // opts.attrs: dialogs that open by an attribute
    if (opts.when) window.addEventListener('resize', change);
  }

  // a11y 1c N5 (2026-10-21): one polite status region for the whole page, outside anything a script re-renders, so
  // results counts and cart changes are read out ("24 products", "Removed: ...").
  var live = null;
  function announce(msg) {
    if (!live) {
      live = document.createElement('div');
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      live.style.cssText = 'position:absolute;width:1px;height:1px;margin:-1px;padding:0;border:0;overflow:hidden;clip:rect(0 0 0 0);clip-path:inset(50%);white-space:nowrap';
      document.body.appendChild(live);
    }
    live.textContent = '';
    setTimeout(function () { live.textContent = msg; }, 80);   // a changed text node is what gets read
  }

  // version 2 (1c): watch() takes opts (when / close / opener / attrs). The 1b file had no version and ignored opts, so
  // a caller that needs one checks for 2: paired with the 1b file (a partial copy into another theme), the request form
  // opened by attribute would stay inert while open, and the desktop filter sidebar would go inert.
  window.agDialog = { watch: watch, version: 2 };
  window.agAnnounce = announce;

  // a11y 1c (2026-10-21): links that act as buttons (role="button", e.g. the shipping-info triggers, whose CSS selects
  // them as <a>) also open on Space, as a real button does; Enter already works on a link.
  document.addEventListener('keydown', function (e) {
    var t = e.target;
    if ((e.key === ' ' || e.key === 'Spacebar') && t && t.matches && t.matches('a[role="button"]')) { e.preventDefault(); t.click(); }
  });

  // a11y 1c (2026-10-21): a quantity stepper press (PDP, and the sticky bar, which presses the PDP's) reads out the new
  // quantity. Several scripts move the number (theme-combined-v2.js, theme.liquid), so this only listens: on window in
  // the capture phase, ahead of any of them stopping the click, and reads the box once they are done.
  window.addEventListener('click', function (e) {
    var b = e.target && e.target.closest && e.target.closest('.quantity-group .qty-plus, .quantity-group .qty-minus');
    var box = b && b.closest('.quantity-group').querySelector('.is-quantity');
    if (box) setTimeout(function () { announce('Quantity ' + box.value); }, 60);
  }, true);

  // a11y 1d (2026-10-22): the skip link focuses <main>, which takes tabindex=-1 only for that and drops it on blur. With a
  // permanent tabindex, WebKit moved focus to <main> on every mouse press inside it; after a keyboard-focused link the
  // page shifted and that click was lost (1c spec). Without this script the link still moves the Tab starting point.
  document.addEventListener('click', function (e) {
    var a = e.target && e.target.closest && e.target.closest('a.skip-link');
    var m = a && document.getElementById('main-content');
    if (!m) return;
    e.preventDefault();
    m.setAttribute('tabindex', '-1');
    m.focus();
    m.addEventListener('blur', function () { m.removeAttribute('tabindex'); }, { once: true });
  });

  function init() {
    watch(document.getElementById('cart-drawer'), function (d) { return d.classList.contains('cart-drawer--open'); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
